class Emitter {
    constructor() {
        /* Properties */
        this.listen = {}; // Listeners
        this.limitStack = 50;
        this.socket = null; // External class (ex: socket.io)
        this.socketSetup = null; // Setup function (reconnect)
        this.socketEmitStack = []; // Waiting to connect
        this.socketOnStack = []; // Waiting to connect
        this.socketPromises = []; // Sock connection
        /*
         *	Secure Process IDs
         */
        let _pid = { index: 0, list: {} };
        /* Add Process ID */
        this._addEntry = (event, obj) => {
            _pid.index++;
            _pid.list[_pid.index] = { event, obj };
            return _pid.index;
        };
        /* Get a process */
        this._getEntry = (id) => {
            if (_pid.list[id] !== undefined) {
                return {
                    event: _pid.list[id].event,
                    obj: _pid.list[id].obj
                };
            } else {
                return false;
            }
        };
        /* Remove a Process ID */
        this._rmEntry = (id) => {
            if (_pid.list[id] !== undefined) {
                const pid = _pid.list[id];
                if (pid.event.indexOf('@') == 0) {
                    /* Socket.on() */
                    if (this.socket != null) {
                        this.socket.removeListener(pid.event.substr(1), pid.obj);
                    }
                } else {
                    /* Emitter.on() */
                    delete this.listen[pid.event][this.listen[pid.event].indexOf(pid.obj)];
                }
                /* Remove the entry */
                delete _pid.list[id];
            }
        };
        /* Attach entry to register */
        this._attachEntry = (id, register) => {
            if (register !== undefined) {
                if (Array.isArray(register)) {
                    register.push(id);
                } else if (register.constructor.name == 'Function') {
                    register(id);
                }
            }
            return id;
        };
        /* Reinstall the socket entries */
        this._setupSocketEntries = () => {
            let pid;
            Object.keys(_pid.list).forEach((id) => {
                pid = _pid.list[id];
                if (pid.event.indexOf('@') == 0) {
                    this.socket.on(pid.event.substr(1), pid.obj);
                }
            });
        };
    }

    /**
     *	Catch an event
     */
    on(event, callback, once) {
        let item,
            pid = null;
        once = this.isset(once) ? once : false;
        /* Detect socket listener */
        if (event.indexOf('@') == 0) {
            if (this.socket != null) {
                this.socket.on(event.substr(1), callback);
                pid = this._addEntry(event, callback);
            } else {
                pid = this._addEntry(event, callback);
                this.socketOnStack.push(pid);
            }
            return (register) => {
                this._attachEntry(pid, register);
            };
        }
        /* The event's type isn't defined */
        if (!this.isset(this.listen[event]) || !this.isset(this.listen[event][0])) {
            /* Create a new type of event */
            item = {
                callback: callback,
                args: null,
                once: once
            };
            this.listen[event] = [item];
            /* Associate PID */
            item.pid = this._addEntry(event, item);
            pid = item.pid;
        } else {
            /*
             *	enable : enable / disable the execution
             *	existEmit : check if the event has propaged
             */
            let enable = true,
                existEmit = false;
            /* Execute the callers */
            this.listen[event].forEach((item, key) => {
                /* The callback is not defined */
                if (item.callback === null) {
                    /* For each item on the stack */
                    item.args.forEach((args) => {
                        if (enable) {
                            /* Execute the callback */
                            callback(args);
                            /* More calls */
                            if (!once) {
                                item.pid = this._addEntry(event, item);
                                item.callback = callback;
                            } else {
                                if (item.args.length == 1) {
                                    delete this.listen[event][key];
                                } else {
                                    item.args.splice(0, 1);
                                }
                                enable = false;
                            }
                        }
                    });
                    /* An event has propaged */
                    existEmit = true;
                }
            });
            /* If 'no emit' then */
            if (!existEmit) {
                /* Add new listener */
                item = {
                    callback: callback,
                    args: null,
                    once: once
                };
                this.listen[event].push(item);
                /* Associate PID */
                item.pid = this._addEntry(event, item);
                pid = item.pid;
            }
        }
        return (register) => {
            this._attachEntry(pid, register);
        };
    }

    /**
     *	Send an event
     */
    emit(event, ...args) {
        /* Detect socket emitter */
        if (event.indexOf('@') == 0) {
            if (this.socket != null) {
                this.socket.emit(event.substr(1), args);
            } else {
                this.socketEmitStack.push({ action: event.substr(1), args });
            }
            return;
        }
        /* The event's type isn't defined or the list is empty */
        if (!this.isset(this.listen[event]) || !this.isset(this.listen[event][0])) {
            /* Create a new type of event */
            this.listen[event] = [
                {
                    callback: null,
                    args: [args],
                    once: false
                }
            ];
        } else {
            /* Execute the listeners */
            this.listen[event].forEach((item, key) => {
                /* If the callback is not defined */
                if (item.callback === null) {
                    /* Security - limit the stack's size */
                    if (item.args.length < this.limitStack || !this.limitStack) {
                        item.args.push(args);
                    }
                } else {
                    /* Execute the callback */
                    item.callback(args);
                    /* Once */
                    if (item.once) {
                        delete this.listen[event][key];
                    }
                }
            });
        }
    }

    /**
     *	Remove listeners
     */
    remove(register) {
        if (Array.isArray(register)) {
            register.forEach((item) => {
                this._rmEntry(item);
            });
        } else {
            this._rmEntry(register);
        }
    }

    /**
     *	Connect with any other class
     */
    connect(setup, now) {
        let entry, promise;
        /* Setup */
        if (this.isset(setup) && typeof setup == 'function') {
            if (now != false || this.socketPromises.length) {
                const socket = setup();
                /* Has on() and emit() methods */
                if (this.isset(socket) && this.isset(socket.on) && this.isset(socket.emit)) {
                    /* Overwrite the current socket */
                    if (socket != this.socket) {
                        this.socket = socket;
                        this.socketSetup = setup;
                        /* Unstack the listeners */
                        while (((entry = this._getEntry(this.socketOnStack.pop())), entry)) {
                            this.socket.on(entry.event.substr(1), entry.obj);
                        }
                    }
                }
            } else {
                this.socketSetup = setup;
            }
        } else if (this.socketSetup != null && this.socket == null) {
            this.socket = this.socketSetup();
            this._setupSocketEntries();
        }
        /* Execute once */
        if (this.socket != null) {
            /* Unstack the emitters */
            while (((entry = this.socketEmitStack.pop()), entry)) {
                this.socket.emit(entry.action, entry.args);
            }
            /* Unstack the promises */
            while (((promise = this.socketPromises.pop()), promise)) {
                promise.resolve();
            }
        }
    }

    /**
     *	Disconnect
     */
    disconnect() {
        let promise;
        if (this.socket != null) {
            this.socket.emit('forceDisconnect');
            this.socketEmitStack = [];
            this.socketOnStack = [];
            this.socket = null;
            /* Unstack the promises */
            while (((promise = this.socketPromises.pop()), promise)) {
                promise.reject();
            }
        }
    }

    /**
     *	Use the socket connection
     */
    sock() {
        return new Promise((resolve, reject) => {
            if (this.socket != null) {
                resolve();
            } else {
                this.socketPromises.push({ resolve, reject });
                this.connect();
            }
        });
    }

    /**
     *	Check if the object exists
     */
    isset(obj) {
        return obj !== undefined ? true : false;
    }
}

export default new Emitter();
